import { NextIntlClientProvider } from "next-intl";
import type { ReactNode } from "react";
import { Analytics } from "@/components/analytics";
import { AnalyticsRoot } from "@/components/analytics/AnalyticsRoot";
import { CspNonceProvider } from "@/components/csp-nonce-provider";
import { Footer } from "@/components/footer";
import { Header } from "@/components/header";
import { Particles } from "@/components/particles";
import { PreloadResources } from "@/components/preload-resources";
import { ReactProvider } from "@/components/react-provider";
import { ScrollToTop } from "@/components/scroll-to-top";
import { SkipToContent } from "@/components/skip-to-content";
import { SmoothScroll } from "@/components/smooth-scroll";
import { SpeedInsights } from "@/components/speed-insights";
import { ThemeProvider } from "@/components/theme-provider";
import { WebVitals } from "@/components/web-vitals";
import "@/app/globals.css";
import "@/styles/scroll-animations.css";
import "@/styles/fix-overflow.css";
// Critical SSR styling: ship the cinema stylesheet with the initial document.
import "@/components/marketing/cinema/experience.css";
// SSR renders the historical KPI section before hydration; ship its CSS at first paint.
import "@/components/marketing/legacy-metrics.css";
// Prevent campaign SSR from flashing unstyled before client hydration.
import "@/components/marketing/black-friday-preview.css";
// Dialog appears on hydration, but must never flash unstyled.
import "@/components/marketing/black-friday-entry-modal.css";

export function AppShell({
  locale,
  messages,
  nonce,
  children,
}: {
  locale: string;
  messages: Record<string, unknown>;
  nonce?: string;
  children: ReactNode;
}) {
  return (
    <CspNonceProvider nonce={nonce}>
      <AnalyticsRoot locale={locale}>
        <NextIntlClientProvider
          locale={locale}
          messages={messages}
          timeZone="America/Sao_Paulo"
          onError={(error) => {
            if (
              error.code === "ENVIRONMENT_FALLBACK" ||
              error.code === "MISSING_MESSAGE"
            )
              return;
            console.error(error);
          }}
          getMessageFallback={({ namespace, key }) =>
            `${namespace ? `${namespace}.` : ""}${key}`
          }
        >
          <ReactProvider>
            <ThemeProvider
              attribute="class"
              defaultTheme="dark"
              enableSystem
              nonce={nonce}
            >
              <div className="fixed inset-0 pointer-events-none z-[9999] opacity-[0.03] mix-blend-overlay" />
              <SkipToContent />
              <PreloadResources />
              <div
                className="relative flex min-h-screen flex-col bg-background text-foreground"
                data-locale={locale}
              >
                <Header />
                <main id="main-content" className="flex-1">
                  <Particles className="fixed inset-0 -z-10" />
                  <SmoothScroll>{children}</SmoothScroll>
                </main>
                <Footer />
                <ScrollToTop />
              </div>
              <Analytics />
              <SpeedInsights />
              <WebVitals />
            </ThemeProvider>
          </ReactProvider>
        </NextIntlClientProvider>
      </AnalyticsRoot>
    </CspNonceProvider>
  );
}
